import type { PublishV1Platform } from '@/lib/publish/publishScope';
import { isPublishV1Platform } from '@/lib/publish/publishScope';
import { PUBLISH_REGISTRY } from '@/lib/publish/publishRegistry';
import type { ConnectedAccountsApiResponse } from '@/lib/publish/connectedAccountsTypes';

/**
 * Phase P7.2 — One row per publish-capable network (config + hydrated connection state).
 * Shipped rows mirror `PUBLISH_REGISTRY` where `shipped` and `publishEndpoint` are set.
 */
export type PublishTarget = {
  platform: PublishV1Platform;
  label: string;
  publishEndpoint: string;
  connected: boolean;
};

type PublishTargetConfig = {
  platform: PublishV1Platform;
  label: string;
  publishEndpoint: string;
};

const PUBLISH_V1_CONFIG: readonly PublishTargetConfig[] = PUBLISH_REGISTRY.flatMap((e) => {
  if (!e.shipped || !e.publishEndpoint) return [];
  if (!isPublishV1Platform(e.platform)) return [];
  return [{ platform: e.platform, label: e.label, publishEndpoint: e.publishEndpoint }];
});

/** @deprecated Use `PUBLISH_V1_CONFIG` / `hydratePublishTargets` — kept for imports that expect `endpoint`. */
export type PublishTargetDefinition = PublishTargetConfig & { endpoint: string };

export const PUBLISH_V1_TARGET_DEFS: readonly PublishTargetDefinition[] = PUBLISH_V1_CONFIG.map(
  (c) => ({ ...c, endpoint: c.publishEndpoint }),
);

export function hydratePublishTargets(
  accounts: ConnectedAccountsApiResponse | undefined,
): readonly PublishTarget[] {
  return PUBLISH_V1_CONFIG.map((c) => ({
    ...c,
    connected: Boolean(accounts?.[c.platform]?.isConnected),
  }));
}

export function labelForPublishPlatform(platform: PublishV1Platform): string {
  const row = PUBLISH_V1_CONFIG.find((c) => c.platform === platform);
  return row?.label ?? platform;
}
